import React, { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { DocumentApi } from '../../../api/DocumentApi';
import { RoutePaths } from '../../../Constants/Common';
import { DocumentType, PolicyDocument } from '../../common/CommonTypes';
import PageTitle from '../../common/PageTitle';
import ProgressButton from '../../common/ProgressButton';
import DocumentDetailsPage from './DocumentDetailsPage';
import LoadingContainer from '../../common/loadingContainer/LoadingContainer';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/hooks';

const ViewDocumentPage = (): JSX.Element => {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(true);
    const [documents, setDocuments] = useState<DocumentType[]>([]);
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(
        undefined
    );
    const [requestButtonStyle, setRequestButtonStyle] = useState(
        'btn btn-primary progress-button disabled'
    );
    const [canDownloadDocument, setCanDownloadDocument] = useState(false);
    const [canViewDocument, setCanViewDocument] = useState(false);
    const [selectedDocumentId, setselectedDocumentId] = useState('');
    const [productReference, setProductReference] = useState('');
    const product = useAppSelector((s) => s.vehicles.selectedPolicy);

    const { t } = useTranslation();

    useEffect(() => {
        loadDocumentDetails();
        if (product != undefined) {
            setProductReference(product.reference);
        }
    }, []);

    useEffect(() => {
        if (canDownloadDocument) downloadDocument(selectedDocumentId);

        if (canViewDocument) viewDocument(selectedDocumentId);
    }, [selectedDocumentId, canDownloadDocument, canViewDocument]);

    const loadDocumentDetails = async () => {
        const successCallback = (documents: DocumentType[]) => {
            setDocuments(documents);
            setLoading(false);
        };

        const failureCallback = () => {
            setLoading(false);
        };

        const token = await getAccessTokenSilently();

        new DocumentApi().getDocumentDetails(
            product.reference,
            token,
            successCallback,
            failureCallback
        );
    };

    const _base64ToArrayBuffer = (base64: string) => {
        const binary_string = window.atob(base64);
        const bytes = Uint8Array.from(binary_string, (c) => c.charCodeAt(0));

        return bytes.buffer;
    };

    const downloadDocument = async (selectedDocumentId) => {
        const successCallback = (policyDocument: PolicyDocument) => {
            const blob = new Blob(
                [_base64ToArrayBuffer(policyDocument.documentData)],
                {
                    type: 'application/pdf'
                }
            );
            downloadPdfFromArrayBytes(selectedDocumentId, blob);
            setLoading(false);
        };

        const failureCallback = () => {
            setLoading(false);
        };

        const token = await getAccessTokenSilently();

        new DocumentApi().getDownloadDocument(
            selectedDocumentId,
            token,
            successCallback,
            failureCallback
        );
    };

    const onDocumentSelectClick = useCallback(
        (selected: boolean, index: number, id: string) => {
            setCanDownloadDocument(false);
            setCanViewDocument(false);

            if (selected) {
                setSelectedIndex(index);
                setselectedDocumentId(id);
                setRequestButtonStyle('btn btn-primary progress-button');
            } else {
                setSelectedIndex(undefined);
                setselectedDocumentId(undefined);
                setRequestButtonStyle(
                    'btn btn-primary progress-button disabled'
                );
            }
        },
        []
    );

    const downloadPdfFromArrayBytes = (fileName = 'testFile.pdf', blob) => {
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', fileName);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    const viewDocument = async (selectedDocumentId) => {
        const successCallback = (policyDocument: PolicyDocument) => {
            const blob = new Blob(
                [_base64ToArrayBuffer(policyDocument.documentData)],
                {
                    type: 'application/pdf'
                }
            );

            const file = new File([blob], selectedDocumentId, {
                lastModified: new Date().getTime(),
                type: blob.type
            });

            openFile(file, selectedDocumentId);
            setLoading(false);
        };

        const failureCallback = () => {
            setLoading(false);
        };

        const token = await getAccessTokenSilently();

        new DocumentApi().getDownloadDocument(
            selectedDocumentId,
            token,
            successCallback,
            failureCallback
        );
    };

    const openFile = (file: File, name: string) => {
        const fileURL = window.URL.createObjectURL(file);
        const link = window.open(fileURL, '_blank', 'noopener,noreferrer');
        if (link) {
            link.opener = null;
            link.name = name;
        }
    };

    const onDownloadDocumentClicked = () => {
        setCanDownloadDocument(true);
        setCanViewDocument(false);
    };

    const onViewDocumentClicked = () => {
        setCanViewDocument(true);
        setCanDownloadDocument(false);
    };

    const hasDocuments = documents && documents.length > 0;

    return (
        <LoadingContainer loading={loading} message={t('loading')}>
            <div className='card page-content'>
                <PageTitle title={`${productReference} - ${t('documents')}`} />
                {hasDocuments ? (
                    <DocumentDetailsPage
                        documents={documents}
                        onDocumentSelectClick={onDocumentSelectClick}
                        selectedIndex={selectedIndex}
                    />
                ) : (
                    <div
                        className='alert alert-warning panel-selection-alert-container mx-2'
                        role='alert'>
                        {t('no-documents')}
                    </div>
                )}
            </div>
            <div className='bottom-navigation'>
                {hasDocuments && (
                    <>
                        <ProgressButton
                            onClick={onDownloadDocumentClicked}
                            navRoute={''}
                            buttonStyle={requestButtonStyle}
                            text={t('download_document')}
                        />
                        <ProgressButton
                            onClick={onViewDocumentClicked}
                            navRoute={''}
                            buttonStyle={requestButtonStyle}
                            text={t('view_document')}
                        />
                    </>
                )}
                <ProgressButton
                    navRoute={RoutePaths.Home}
                    buttonStyle='btn btn-secondary progress-button'
                    text={t('back')}
                />
            </div>
        </LoadingContainer>
    );
};

export default ViewDocumentPage;
